/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import axios from 'axios';
import { Left, Right } from 'purify-ts';
import Vue from 'vue';

import * as api from '@codegrade/api';

import client from '@/client';
import { NONEXISTENT } from '@/constants';
import * as models from '@/models';
import { Rubric, RubricResult } from '@/models/rubric';
import * as utils from '@/utils';

import { store } from '..';
import * as types from '../mutation-types';
import { AssignmentsStore } from './assignments';
import { SubmissionsStore } from './submissions';

const getters = {
    rubrics: state => state.rubrics,
    results: state => state.results,
};

const loaders = {
    rubrics: {},
    results: {},
};

const actions = {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async loadRubric({ commit, state }, { assignmentId, force }) {
        if (state.rubrics[assignmentId] && !force) {
            return state.rubrics[assignmentId];
        }

        if (!Object.hasOwnProperty.call(loaders.rubrics, assignmentId)) {
            loaders.rubrics[assignmentId] = axios
                .get(`/api/v1/assignments/${assignmentId}/rubrics/`)
                .then(
                    response => {
                        delete loaders.rubrics[assignmentId];
                        commit(types.SET_RUBRIC, {
                            assignmentId,
                            rubric: response.data,
                        });
                        return response;
                    },
                    err => {
                        delete loaders.rubrics[assignmentId];
                        commit(types.SET_RUBRIC, { assignmentId, rubric: NONEXISTENT });
                        throw err;
                    },
                );
        }

        return loaders.rubrics[assignmentId];
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    copyRubric({ commit }, { fromAssignment, intoAssignment }) {
        return client.assignment
            .copyRubric({
                assignmentId: intoAssignment.id,
                requestBody: { oldAssignmentId: fromAssignment.id },
            })
            .chain(response =>
                AssignmentsStore.loadSingleAssignment({
                    assignmentId: intoAssignment.id,
                    courseId: intoAssignment.courseId,
                    force: true,
                }).then(() => Right(response), Left),
            )
            .chain(async response => {
                commit(types.SET_RUBRIC, {
                    assignmentId: intoAssignment.id,
                    rubric: response.cgResult,
                });
                return Right(response);
            });
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateRubricInStore({ commit }, { assignmentId, rubric }) {
        commit(types.SET_RUBRIC, {
            assignmentId,
            rubric,
        });
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateRubric({ commit }, { assignmentId, rubric, maxPoints }) {
        return client.assignment
            .putRubric({
                assignmentId,
                requestBody: {
                    rows: rubric.rows,
                    maxPoints,
                },
            })
            .chain(response =>
                AssignmentsStore.loadSingleAssignment({
                    assignmentId,
                    force: true,
                }).then(() => Right(response), Left),
            )
            .chain(async response => {
                commit(types.SET_RUBRIC, {
                    assignmentId,
                    rubric: rubric.updateFromApiServerData(response.cgResult),
                });
                return Right(response);
            });
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    deleteRubric({ commit }, { assignmentId }) {
        return client.assignment
            .deleteRubric({ assignmentId })
            .chain(response =>
                AssignmentsStore.loadSingleAssignment({
                    assignmentId,
                    force: true,
                }).then(() => Right(response), Left),
            )
            .map(response =>
                response.addCallbacks({
                    onAfterSuccess: () => {
                        commit(types.SET_RUBRIC, {
                            assignmentId,
                            rubric: NONEXISTENT,
                        });
                    },
                }),
            );
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    clearRubric({ commit, state }, { assignmentId }) {
        if (Object.hasOwnProperty.call(state.rubrics, assignmentId)) {
            commit(types.CLEAR_RUBRIC, { assignmentId });
        }
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    loadResult({ commit, state }, { assignmentId, submissionId, force }) {
        if (state.results[submissionId] && !force) {
            return Promise.resolve();
        }

        if (!Object.hasOwnProperty.call(loaders.results, submissionId)) {
            loaders.results[submissionId] = SubmissionsStore.loadSingleSubmission({
                assignmentId,
                submissionId,
            }).then(responseOrError =>
                responseOrError.caseOf({
                    Right: response => {
                        utils.vueDelete(loaders.results, submissionId);
                        response.cgResult.rubricResult
                            .filter(r => r.rubric.rows.length !== 0)
                            .caseOf({
                                Just: result => {
                                    commit(types.SET_RUBRIC_RESULT, {
                                        submissionId,
                                        result,
                                    });
                                },
                                Nothing: () => {
                                    commit(types.SET_RUBRIC_RESULT, {
                                        submissionId,
                                        result: NONEXISTENT,
                                    });
                                },
                            });
                        return response;
                    },
                    Left: err => {
                        utils.vueDelete(loaders.results, submissionId);
                        commit(types.SET_RUBRIC_RESULT, {
                            submissionId,
                            result: NONEXISTENT,
                        });
                        throw err;
                    },
                }),
            );
        }

        return loaders.results[submissionId];
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    clearResult({ commit, state }, { submissionId, submissionIds }) {
        let ids = submissionIds;
        if (submissionIds == null) {
            ids = [submissionId];
        }
        ids.forEach(id => {
            if (Object.hasOwnProperty.call(state.results, id)) {
                commit(types.CLEAR_RUBRIC_RESULT, { submissionId: id });
            }
        });
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async updateRubricResult({ commit, state, dispatch }, { assignmentId, submissionId, result }) {
        await Promise.all([
            SubmissionsStore.loadSingleSubmission({ assignmentId, submissionId }),
            dispatch('loadRubric', { assignmentId }),
        ]);

        const rubric = state.rubrics[assignmentId];
        result.validate().throwOnError();

        const selected = { ...result.selected };
        rubric.rows.forEach(row => {
            if (row.locked) {
                delete selected[row.id];
            }
        });

        return await client.submission
            .patchRubricResult({
                submissionId,
                copyLockedItems: true,
                requestBody: {
                    items: Object.entries(selected).map(([rowId, item]) => ({
                        rowId: Number(rowId),
                        itemId: item.id,
                        multiplier: item.multiplier,
                    })),
                },
            })
            .map(response =>
                response.addCallbacks({
                    onSuccess: ({ cgResult }) => {
                        SubmissionsStore.commitSingleSubmission({
                            submission: models.Submission.fromApiServerData(cgResult),
                        });
                        cgResult.rubricResult.ifJust(rubricResult =>
                            commit(types.SET_RUBRIC_RESULT, {
                                submissionId,
                                assignmentId,
                                result: RubricResult.fromApiServerData(submissionId, rubricResult),
                            }),
                        );
                    },
                }),
            );
    },
};

// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const mutations = {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.SET_RUBRIC](state, { assignmentId, rubric }) {
        let rubricModel = rubric;
        if (Array.isArray(rubric) && rubric.every(row => row instanceof api.RubricRowBase)) {
            rubricModel = Rubric.fromApiServerData(rubric);
        } else if (rubricModel !== NONEXISTENT && !(rubric instanceof Rubric)) {
            rubricModel = Rubric.fromServerData(rubric);
        }
        Vue.set(state.rubrics, assignmentId, rubricModel);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.CLEAR_RUBRIC](state, { assignmentId }) {
        Vue.delete(state.rubrics, assignmentId);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.SET_RUBRIC_RESULT](state, { submissionId, assignmentId, result }) {
        let rubricModel;
        let resultModel = result;

        if (result !== NONEXISTENT) {
            if (result instanceof api.WorkRubricResultAsJSON) {
                rubricModel = Rubric.fromApiServerData(result.rubrics);
                resultModel = RubricResult.fromApiServerData(submissionId, result);
            } else if (!(result instanceof RubricResult)) {
                rubricModel = Rubric.fromServerData(result.rubrics);
                resultModel = RubricResult.fromServerData(submissionId, result);
            }
        }

        if (assignmentId != null && rubricModel != null && state.rubrics[assignmentId] == null) {
            Vue.set(state.rubrics, assignmentId, rubricModel);
        }
        Vue.set(state.results, submissionId, resultModel);
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    [types.CLEAR_RUBRIC_RESULT](state, { submissionId }) {
        Vue.delete(state.results, submissionId);
    },

    /** Clear the entire rubric store. */
    [types.CLEAR_RUBRIC_ALL](state) {
        Vue.set(state, 'rubrics', {});
        Vue.set(state, 'results', {});
    },
};

export default {
    namespaced: true,
    state: {
        rubrics: {},
        results: {},
    },
    getters,
    actions,
    mutations,
};

utils.signal.AFTER_USER_CHANGED.register(async () => {
    store.commit(`rubrics/${types.CLEAR_RUBRIC_ALL}`, null, { root: true });
});
